/**
 * Achievement Display Component
 * Badge-style animations for awards, certifications, and company achievements
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { animated, useSpring } from '@react-spring/web';
import { 
  useScrollAnimation, 
  useAnimationConfig, 
  useReducedMotion, 
  usePerformanceMonitor,
  AnimatedContainer
} from '../lib/animations';
import { 
  Award, Trophy, Star, Medal, Shield, CheckCircle, 
  Calendar, MapPin, Users, Building, Zap, Heart,
  ExternalLink, Info, ChevronRight
} from 'lucide-react';

// Achievement types
export type AchievementType = 
  | 'award' 
  | 'certification' 
  | 'milestone' 
  | 'recognition' 
  | 'partnership' 
  | 'achievement';

// Achievement item interface
export interface Achievement {
  id: string;
  title: string;
  description: string;
  type: AchievementType;
  year?: number;
  date?: string;
  organization?: string;
  location?: string;
  icon?: React.ComponentType<any>;
  image?: string;
  color?: string;
  level?: 'bronze' | 'silver' | 'gold' | 'platinum';
  verified?: boolean;
  link?: string;
  details?: string[];
  stats?: { label: string; value: string }[];
}

// Badge configuration
export interface BadgeConfig {
  style: 'modern' | 'classic' | 'minimal' | 'premium' | 'certificate';
  size: 'small' | 'medium' | 'large';
  animation: 'bounce' | 'glow' | 'pulse' | 'rotate' | 'scale' | 'flip';
  showLevel?: boolean;
  showYear?: boolean;
  showOrganization?: boolean;
  interactive?: boolean;
  hoverEffect?: 'lift' | 'glow' | 'scale' | 'tilt' | 'shine';
}

interface AchievementBadgeProps {
  achievement: Achievement;
  config: BadgeConfig;
  className?: string;
  onClick?: (achievement: Achievement) => void;
  animationDelay?: number;
}

// Level colors and styles
const levelStyles = {
  bronze: {
    gradient: 'from-amber-600 to-amber-800',
    glow: 'shadow-amber-500/50',
    border: 'border-amber-400',
    text: 'text-amber-100'
  },
  silver: {
    gradient: 'from-gray-400 to-gray-600',
    glow: 'shadow-gray-500/50',
    border: 'border-gray-300',
    text: 'text-gray-100'
  },
  gold: {
    gradient: 'from-yellow-400 to-yellow-600',
    glow: 'shadow-yellow-500/50',
    border: 'border-yellow-300',
    text: 'text-yellow-100'
  },
  platinum: {
    gradient: 'from-purple-500 to-purple-700',
    glow: 'shadow-purple-500/50',
    border: 'border-purple-400',
    text: 'text-purple-100'
  }
};

// Type icons mapping
const typeIcons = {
  award: Award,
  certification: Shield,
  milestone: Trophy,
  recognition: Star,
  partnership: Users,
  achievement: Medal
};

// Individual Achievement Badge Component
export const AchievementBadge: React.FC<AchievementBadgeProps> = ({
  achievement,
  config,
  className = '',
  onClick,
  animationDelay = 0
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const animationConfig = useAnimationConfig();
  const isReducedMotion = useReducedMotion();
  const { trackAnimation } = usePerformanceMonitor();

  const IconComponent = achievement.icon || typeIcons[achievement.type];
  const levelStyle = achievement.level ? levelStyles[achievement.level] : levelStyles.gold;

  // Spring animations for interactions
  const badgeSpring = useSpring({
    scale: isClicked ? 0.95 : isHovered ? 1.05 : 1,
    rotateY: config.hoverEffect === 'tilt' && isHovered ? 5 : 0,
    rotateX: config.hoverEffect === 'tilt' && isHovered ? -2 : 0,
    y: config.hoverEffect === 'lift' && isHovered ? -8 : 0,
    config: {
      tension: 300,
      friction: 30
    },
    immediate: isReducedMotion
  });

  // Glow effect spring
  const glowSpring = useSpring({
    opacity: isHovered && config.hoverEffect === 'glow' ? 1 : 0,
    scale: isHovered && config.hoverEffect === 'glow' ? 1.1 : 1,
    config: {
      tension: 200,
      friction: 50
    },
    immediate: isReducedMotion
  });

  // Entrance animation variants
  const entranceVariants = {
    hidden: {
      opacity: 0,
      scale: 0.5,
      rotate: config.animation === 'rotate' ? -180 : 0,
      y: config.animation === 'bounce' ? 50 : 0
    },
    visible: {
      opacity: 1,
      scale: 1,
      rotate: 0,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30,
        delay: animationDelay
      }
    }
  };

  // Continuous animation variants
  const continuousVariants = {
    pulse: {
      scale: [1, 1.05, 1],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }
    },
    glow: {
      boxShadow: [
        '0 0 0px rgba(59, 130, 246, 0)',
        '0 0 20px rgba(59, 130, 246, 0.5)',
        '0 0 0px rgba(59, 130, 246, 0)'
      ],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }
    }
  };

  // Size classes
  const sizeClasses = {
    small: 'w-16 h-16 p-2',
    medium: 'w-24 h-24 p-3',
    large: 'w-32 h-32 p-4'
  };

  // Style classes
  const getStyleClasses = () => {
    const base = 'relative rounded-full flex items-center justify-center cursor-pointer transition-all duration-300';
    
    switch (config.style) {
      case 'modern':
        return `${base} bg-gradient-to-br ${levelStyle.gradient} ${levelStyle.glow} shadow-lg border-2 ${levelStyle.border}`;
      case 'classic':
        return `${base} bg-gradient-to-br from-blue-600 to-blue-800 shadow-xl border-4 border-blue-300`;
      case 'minimal':
        return `${base} bg-white shadow-md border border-gray-200 text-gray-700`;
      case 'premium':
        return `${base} bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 shadow-2xl border-2 border-white`;
      case 'certificate':
        return `${base} bg-gradient-to-br from-emerald-500 to-emerald-700 shadow-lg border-2 border-emerald-300`;
      default:
        return `${base} bg-gradient-to-br ${levelStyle.gradient}`;
    }
  };

  const handleClick = () => {
    setIsClicked(true);
    setTimeout(() => setIsClicked(false), 150);
    if (onClick) onClick(achievement);
  };

  return (
    <div className={`relative ${className}`}>
      {/* Glow Effect Background */}
      {config.hoverEffect === 'glow' && (
        <animated.div
          style={glowSpring}
          className={`absolute inset-0 rounded-full ${levelStyle.gradient} blur-md -z-10`}
        />
      )}

      {/* Main Badge */}
      <animated.div
        style={badgeSpring}
        className={`${getStyleClasses()} ${sizeClasses[config.size]}`}
        onMouseEnter={() => config.interactive && setIsHovered(true)}
        onMouseLeave={() => config.interactive && setIsHovered(false)}
        onClick={handleClick}
      >
        <motion.div
          variants={entranceVariants}
          initial="hidden"
          animate="visible"
          className="flex flex-col items-center justify-center w-full h-full"
        >
          {/* Icon */}
          <motion.div
            animate={!isReducedMotion && config.animation === 'pulse' ? continuousVariants.pulse : {}}
            className={`${config.style === 'minimal' ? 'text-gray-700' : 'text-white'}`}
          >
            <IconComponent size={config.size === 'small' ? 20 : config.size === 'medium' ? 28 : 36} />
          </motion.div>

          {/* Level Indicator */}
          {config.showLevel && achievement.level && (
            <motion.div
              className={`absolute -top-1 -right-1 w-6 h-6 rounded-full bg-white flex items-center justify-center text-xs font-bold ${
                achievement.level === 'gold' ? 'text-yellow-600' :
                achievement.level === 'silver' ? 'text-gray-600' :
                achievement.level === 'bronze' ? 'text-amber-600' :
                'text-purple-600'
              }`}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: animationDelay + 0.3, type: 'spring' }}
            >
              {achievement.level === 'gold' ? '🥇' :
               achievement.level === 'silver' ? '🥈' :
               achievement.level === 'bronze' ? '🥉' : '💎'}
            </motion.div>
          )}

          {/* Verification Badge */}
          {achievement.verified && (
            <motion.div
              className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center"
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: animationDelay + 0.5, type: 'spring' }}
            >
              <CheckCircle size={12} className="text-white" />
            </motion.div>
          )}
        </motion.div>

        {/* Shine Effect */}
        {config.hoverEffect === 'shine' && isHovered && (
          <motion.div
            className="absolute inset-0 rounded-full overflow-hidden"
            initial={{ x: '-100%' }}
            animate={{ x: '100%' }}
            transition={{ duration: 0.6, ease: 'easeInOut' }}
          >
            <div className="w-full h-full bg-gradient-to-r from-transparent via-white/30 to-transparent transform skew-x-12" />
          </motion.div>
        )}
      </animated.div>

      {/* Year Badge */}
      {config.showYear && achievement.year && (
        <motion.div
          className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded-full"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: animationDelay + 0.4 }}
        >
          {achievement.year}
        </motion.div>
      )}
    </div>
  );
};

// Achievement Grid Component
interface AchievementGridProps {
  achievements: Achievement[];
  config: BadgeConfig;
  title?: string;
  subtitle?: string;
  className?: string;
  layout?: 'grid' | 'row' | 'masonry';
  staggerDelay?: number;
  onAchievementClick?: (achievement: Achievement) => void;
  triggerOnScroll?: boolean;
  threshold?: number;
}

export const AchievementGrid: React.FC<AchievementGridProps> = ({
  achievements,
  config,
  title,
  subtitle,
  className = '',
  layout = 'grid',
  staggerDelay = 0.1,
  onAchievementClick,
  triggerOnScroll = true,
  threshold = 0.1
}) => {
  const scrollAnimation = useScrollAnimation(threshold, true);
  const isVisible = triggerOnScroll ? scrollAnimation.isVisible : true;

  // Layout classes
  const layoutClasses = {
    grid: 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6',
    row: 'flex flex-wrap justify-center gap-6',
    masonry: 'columns-2 md:columns-3 lg:columns-4 gap-6 space-y-6'
  };

  return (
    <AnimatedContainer
      ref={triggerOnScroll ? scrollAnimation.ref : undefined}
      className={`w-full ${className}`}
      triggerOnScroll={false}
    >
      {/* Header */}
      {(title || subtitle) && (
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : -20 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          {title && (
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {title}
            </h2>
          )}
          {subtitle && (
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {subtitle}
            </p>
          )}
        </motion.div>
      )}

      {/* Achievement Grid */}
      <div className={layoutClasses[layout]}>
        {achievements.map((achievement, index) => (
          <motion.div
            key={achievement.id}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ 
              opacity: isVisible ? 1 : 0, 
              scale: isVisible ? 1 : 0.8 
            }}
            transition={{
              duration: 0.5,
              delay: index * staggerDelay,
              ease: 'easeOut'
            }}
          >
            <AchievementBadge
              achievement={achievement}
              config={config}
              onClick={onAchievementClick}
              animationDelay={index * staggerDelay}
            />
          </motion.div>
        ))}
      </div>
    </AnimatedContainer>
  );
};

// Achievement Detail Modal
interface AchievementDetailProps {
  achievement: Achievement | null;
  isOpen: boolean;
  onClose: () => void;
}

export const AchievementDetail: React.FC<AchievementDetailProps> = ({
  achievement,
  isOpen,
  onClose
}) => {
  if (!achievement) return null;

  const IconComponent = achievement.icon || typeIcons[achievement.type];
  const levelStyle = achievement.level ? levelStyles[achievement.level] : levelStyles.gold;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="bg-white rounded-2xl p-8 max-w-md w-full max-h-[80vh] overflow-y-auto"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center mb-6">
              <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${levelStyle.gradient} flex items-center justify-center mr-4`}>
                <IconComponent size={32} className="text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-1">
                  {achievement.title}
                </h3>
                {achievement.organization && (
                  <p className="text-gray-600">{achievement.organization}</p>
                )}
              </div>
            </div>

            {/* Description */}
            <p className="text-gray-700 mb-6">
              {achievement.description}
            </p>

            {/* Details */}
            {achievement.details && achievement.details.length > 0 && (
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">Details</h4>
                <ul className="space-y-2">
                  {achievement.details.map((detail, index) => (
                    <li key={index} className="flex items-start">
                      <ChevronRight size={16} className="text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Stats */}
            {achievement.stats && achievement.stats.length > 0 && (
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">Statistics</h4>
                <div className="grid grid-cols-2 gap-4">
                  {achievement.stats.map((stat, index) => (
                    <div key={index} className="text-center p-3 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                      <div className="text-sm text-gray-600">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Meta Information */}
            <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-6">
              {achievement.year && (
                <div className="flex items-center">
                  <Calendar size={16} className="mr-1" />
                  {achievement.year}
                </div>
              )}
              {achievement.location && (
                <div className="flex items-center">
                  <MapPin size={16} className="mr-1" />
                  {achievement.location}
                </div>
              )}
              {achievement.verified && (
                <div className="flex items-center text-green-600">
                  <CheckCircle size={16} className="mr-1" />
                  Verified
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              {achievement.link && (
                <a
                  href={achievement.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center"
                >
                  <ExternalLink size={16} className="mr-2" />
                  View Details
                </a>
              )}
              <button
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
              >
                Close
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Preset achievement configurations
export const achievementPresets = {
  awards: {
    style: 'premium' as const,
    size: 'large' as const,
    animation: 'glow' as const,
    showLevel: true,
    showYear: true,
    interactive: true,
    hoverEffect: 'glow' as const
  },
  
  certifications: {
    style: 'certificate' as const,
    size: 'medium' as const,
    animation: 'scale' as const,
    showOrganization: true,
    interactive: true,
    hoverEffect: 'lift' as const
  },
  
  milestones: {
    style: 'modern' as const,
    size: 'medium' as const,
    animation: 'bounce' as const,
    showYear: true,
    interactive: true,
    hoverEffect: 'tilt' as const
  }
};

export default AchievementBadge;